import Loader from '../../../components/common/Loader';
import { InfoState } from '../../../components/ui';
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button } from '../../../components/ui';
import { Pagination, Search, TableHeader, TableBody } from '../../../components/table';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { getPaymentHistory } from '../../../services/user/Settings.services';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function Payment() {
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const { timeZone } = useTimezone();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [allPaymentHistory, setAllPaymentHistory] = useState([]);

  useEffect(() => {
    fetchPaymentHistory();
  }, []); // Fetch all data once

  const fetchPaymentHistory = async () => {
    setLoading(true); setUnavailable(false);
    try {
      const response = await getPaymentHistory();
      if (response?.success && response?.data) {
        setAllPaymentHistory(response.data);
      } else { setUnavailable(true); }
    } catch (error) {
      console.warn('[Payment history]', error); setUnavailable(true);
    } finally { setLoading(false); }
  };

  // Filter and paginate data on the frontend
  const { paginatedData, totalPages, totalCount } = useMemo(() => {
    // Filter data based on search term
    const filtered = allPaymentHistory.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.payment_uid?.toLowerCase().includes(searchLower) ||
        item.course_name?.toLowerCase().includes(searchLower) ||
        item.amount?.toString().includes(searchLower) ||
        item.payment_method?.toLowerCase().includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower) ||
        formatDate(item.created_at).toLowerCase().includes(searchLower)
      );
    });

    // Calculate pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginated = filtered.slice(startIndex, endIndex);
    const totalPagesCalc = Math.max(1, Math.ceil(filtered.length / limit));

    return {
      paginatedData: paginated,
      totalPages: totalPagesCalc,
      totalCount: filtered.length
    };
  }, [allPaymentHistory, searchTerm, page, limit, timeZone]);

  // Reset to first page when search term or limit changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, limit]);

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--success-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize tw-bg-emerald-50 tw-border-emerald-600 tw-text-emerald-600"}>Completed</span>;
      case 'pending':
        return <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--warning-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize tw-bg-amber-50 tw-border-amber-600 tw-text-amber-600"}>Pending</span>;
      case 'failed':
        return <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] !tw-text-[var(--danger-color)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize tw-bg-rose-50 tw-border-rose-600 tw-text-rose-600"}>Failed</span>;
      default:
        return <span className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] [&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize tw-bg-slate-50 tw-border-slate-500 tw-text-slate-600"}>Unknown</span>;
    }
  };

  const truncateTransactionId = (payment_uid) => {
    return payment_uid.substring(0, 11);
  };

  function formatDate(dateString) {
    return formatUtcInTimeZone(dateString, timeZone, 'MMM DD, YYYY');
  }

  return (
    <>
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-py-4"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full tw-mb-4">
          <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center">
            <h4 className="tw-mb-2 md:tw-mb-0">Payment History</h4>
          </div>
        </div>

        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-flex-wrap">
            <div className="tw-w-full tw-max-w-[350px]">
              <Search
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPageReset={() => setPage(1)}
                className="tw-rounded-xl"
              />
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {loading ? <Loader variant="table" rows={5} columns={5} /> : unavailable ? <InfoState title="This list is temporarily unavailable" onRetry={fetchPaymentHistory} /> : (<Table bordered responsive style={{ minWidth: '1200px' }}>
              <TableHeader className="[--tw-bg-opacity:1] tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-table-primary">
                <tr>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>SL No.</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Transaction ID</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Course Name</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Original Price</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Points Used</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Points Discount</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Online Amount</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Purchase Type</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Status</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }}>Payment Date</th>
                  <th style={{ whiteSpace: 'nowrap', paddingInline: '10px' }} className="tw-text-center">Action</th>
                </tr>
              </TableHeader>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="tw-text-center">{((page - 1) * limit) + index + 1}</td>
                      <td>{truncateTransactionId(item.payment_uid)}</td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div className="[display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-text-ellipsis tw-whitespace-normal tw-max-w-[400px] tw-leading-[1.4] tw-min-h-[calc(1.4em_*_2)] truncate-2-line" title={item.course_name}>
                          {item.course_name}
                        </div>
                      </td>
                      <td>{item.currency} {Number(item.original_course_price ?? item.amount).toFixed(2)}</td>
                      <td>{item.points_used || 0}</td>
                      <td>{item.currency} {Number(item.points_discount_amount || 0).toFixed(2)}</td>
                      <td>{item.currency} {Number(item.online_payment_amount ?? item.amount).toFixed(2)}</td>
                      <td>{{ FREE: 'Free', PAYMENT: 'Online Payment', POINTS: 'Points', PAYMENT_AND_POINTS: 'Payment + Points' }[item.purchase_type] || 'Online Payment'}</td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td>{formatDate(item.created_at)}</td>
                      <td className="tw-flex tw-justify-center tw-items-center tw-gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="tw-rounded-xl"
                          disabled={!item.receipt_url}
                          style={{
                            cursor: !item.receipt_url ? 'not-allowed' : 'pointer',
                            pointerEvents: !item.receipt_url ? 'auto' : 'auto',
                            width: '36px',
                            height: '36px',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          href={item.receipt_url || '#'}
                          target={item.receipt_url ? "_blank" : undefined}
                          onClick={!item.receipt_url ? (e) => e.preventDefault() : undefined}
                          title="Download Receipt"
                        >
                          <Icon icon="mdi:download" width="20" height="20" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="tw-p-0">
                      <NoData message="No payment records found." />
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>)}
          </div>

          <Pagination
            page={page}
            pageSize={limit}
            buttonVariant="outline-secondary"
            buttonSize="sm"
            totalRecords={totalCount}
            totalPages={totalPages}
            disabled={loading || unavailable}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
          />
        </div>
      </div>
    </>
  );
}

export default Payment;
